import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { isNull } from 'lodash-es';

function getLabel({ msrp, esp, lowEnd, highEnd, isESP, isMSRP, index }, keyPointsIndex) {
  if (isESP) {
    return esp;
  } else if (isMSRP && !!msrp) {
    return msrp;
  } else if (index === keyPointsIndex.lowEnd) {
    return lowEnd;
  } else if (index === keyPointsIndex.highEnd) {
    return highEnd;
  }
  return '';
}

/**
 * @deprecated use client/site-modules/shared/components/price-checker/price-checker-promo-chart/pricing-chart.jsx instead
 */
export const PricingChart = ({
  pricingData,
  isMobile = false,
  dataPoints = [1, 2, 4, 6, 7, 9, 12, 14, 11, 12, 14, 11, 12, 8, 7, 6, 6, 5, 10],
  keyPointsIndex = {
    lowEnd: 7,
    highEnd: 13,
    esp: 10,
    msrp: 18,
  },
  primaryRangeColor = '#8ccb00',
  secondaryRangeColor = '#cccccc',
  greatRangeColor = '',
  goodRangeColor = '',
  fairRangeColor = '',
  columnWidth = {
    mobile: 16,
    desktop: 27,
  },
  columnPadding = 3,
  chartHeight = 250,
  yRate = 5,
  yStartingPoint = 150,
  strokeLength = {
    desktop: 17,
    mobile: 17,
  },
  customLabels = [],
  customPriceLabels = [],
  selectedPriceIndex = null,
  showMarketRange = true,
  selectedPricePointRadius = null,
  highlightPrimaryRange = true,
  labelVerticalShift = {
    desktop: 15,
    mobile: 15,
  },
  isArrowPositionMark = false,
  hideNoIndexArrow = false,
  arrowSize = 12,
  isCurvedLine = false,
  filterRandomInt = 0,
  gradientConfig = null,
  gradientConfigLayout = null,
  xAxisColor = '#cccccc',
  dealerPriceIndex = null,
  dealerPrice = null,
  showPolyline = false,
}) => {
  const { esp, msrp, lowEnd, highEnd } = pricingData;
  const device = isMobile ? 'mobile' : 'desktop';
  const currentColumnWidth = columnWidth[device];
  const chartWidth = currentColumnWidth * dataPoints.length + columnPadding * (dataPoints.length - 1);
  const maxColumnHeight = Math.max(...dataPoints) * yRate;
  const singleColSpace = currentColumnWidth + columnPadding;
  const greenRangeFirstColStart = keyPointsIndex.lowEnd * singleColSpace;
  const greenRangeFirstColMiddle = greenRangeFirstColStart + currentColumnWidth / 2;
  const greenRangeLastColStart = keyPointsIndex.highEnd * singleColSpace;
  const greenRangeLastColMiddle = greenRangeLastColStart + currentColumnWidth / 2;
  const greenRangeLastColEnd = greenRangeLastColStart + currentColumnWidth - 1;
  const isSelectedIndexDefined = !isNull(selectedPriceIndex);

  let ariaLabel = esp ? `Edmunds suggested price is ${esp}.` : '';
  ariaLabel += lowEnd && highEnd ? ` The market range is ${lowEnd} to ${highEnd}.` : '';
  ariaLabel += msrp ? ` The MSRP is ${msrp}.` : '';
  ariaLabel = ariaLabel || 'Compare offer';

  // circle data
  const r1 = selectedPricePointRadius || (currentColumnWidth * 0.75) / 2;
  const r2 = r1 / 2;

  const halfArrowSize = arrowSize / 2;
  const columnsEdge = singleColSpace * dataPoints.length - (isMobile ? halfArrowSize + 1 : 0); // minus (halfArrowSize + 1) to fit on mobile
  const selectedIndexXStartCoord = selectedPriceIndex * singleColSpace;
  const selectedIndexXMiddleCoord = selectedIndexXStartCoord + currentColumnWidth / 2;

  return (
    <div className="pricing-chart text-center">
      <svg
        width="100%"
        height={chartHeight}
        viewBox={`0 0 ${chartWidth} ${chartHeight}`}
        xmlns="http://www.w3.org/2000/svg"
        role="img"
        aria-label={ariaLabel}
      >
        {!!gradientConfigLayout && (
          <defs>
            <linearGradient
              id={`graph-gradient-${filterRandomInt}`}
              x1="100%"
              y1="0%"
              x2={0}
              y2={0}
              gradientUnits="userSpaceOnUse"
            >
              <stop offset="0%" style={{ stopColor: 'rgba(255, 130, 24, 0.4)' }} />
              <stop offset="37%" style={{ stopColor: 'rgba(0, 194, 118, 0.4)' }} />
              <stop offset="68%" style={{ stopColor: 'rgba(0, 152, 194, 0.4)' }} />
              <stop offset="100%" style={{ stopColor: 'rgba(0, 126, 229, 0.4)' }} />
            </linearGradient>
          </defs>
        )}
        {/* X axis */}
        <line x1="-1%" y1={yStartingPoint + 1} x2="101%" y2={yStartingPoint + 1} stroke={xAxisColor} />
        {/* Primary range X axis highlight */}
        {highlightPrimaryRange && (
          <line
            x1={greenRangeFirstColStart}
            y1={yStartingPoint + 1}
            x2={greenRangeLastColEnd}
            y2={yStartingPoint + 1}
            stroke="#999999"
            strokeWidth={2}
          />
        )}
        <g fill={gradientConfigLayout ? `url(#graph-gradient-${filterRandomInt})` : null}>
          {dataPoints.map((y, index) => {
            const isESP = index === keyPointsIndex.esp;
            const isMSRP = index === keyPointsIndex.msrp;
            const xVal = index * singleColSpace;
            const yVal = y * yRate;
            const isInMainRange = index >= keyPointsIndex.lowEnd && index <= keyPointsIndex.highEnd;
            let color = isInMainRange ? primaryRangeColor : secondaryRangeColor;
            const isInGreatRange = index < keyPointsIndex.lowEnd && !!greatRangeColor;
            const isInGoodRange =
              index >= keyPointsIndex.lowEnd && index < keyPointsIndex.middleEnd && !!goodRangeColor;
            const isInFairRange =
              index >= keyPointsIndex.middleEnd && index <= keyPointsIndex.highEnd && !!fairRangeColor;
            if (isInGreatRange) {
              color = greatRangeColor;
            } else if (isInGoodRange) {
              color = goodRangeColor;
            } else if (isInFairRange) {
              color = fairRangeColor;
            }
            let labelEl = null;
            const label = getLabel({ msrp, esp, lowEnd, highEnd, isESP, isMSRP, index }, keyPointsIndex);
            const customPriceLabel = customPriceLabels.find(({ index: labelIndex }) => labelIndex === index);

            // for low-end, high-end and msrp values
            if (label && !isESP) {
              const x1 = xVal + currentColumnWidth / 2;
              const x2 = x1 - 20;

              labelEl = (
                <g>
                  {isMSRP && isMobile ? (
                    <polyline
                      points={`${x1},${yStartingPoint} ${x1},${yStartingPoint + 8} ${x2},${
                        yStartingPoint + 8
                      } ${x2},${yStartingPoint + 18}`}
                      fill="none"
                      stroke="#cccccc"
                    />
                  ) : (
                    <line
                      x1={x1}
                      y1={yStartingPoint}
                      x2={x1}
                      y2={yStartingPoint + strokeLength[device]}
                      stroke="#cccccc"
                    />
                  )}
                  <text
                    fill="#555555"
                    textAnchor="middle"
                    dominantBaseline="middle"
                    x={isMSRP && isMobile ? x2 : x1}
                    y={yStartingPoint + strokeLength[device] + 15}
                    className={classNames({ 'fw-bold': !isMSRP, medium: isMSRP })}
                  >
                    {label}
                  </text>
                  {isMSRP && (
                    <text
                      fill="#555555"
                      textAnchor="middle"
                      dominantBaseline="middle"
                      x={isMSRP && isMobile ? x2 : x1}
                      y={yStartingPoint + strokeLength[device] + 35}
                      className="medium"
                    >
                      MSRP
                    </text>
                  )}
                </g>
              );
            }

            if (label && isESP) {
              const x1 = xVal + currentColumnWidth / 2;
              const chartCenter = chartWidth / 2;
              labelEl = (
                <g>
                  <circle cx={x1} cy={yStartingPoint} r={6} fill="#999999" />
                  <polyline
                    points={`${x1},${yStartingPoint} ${x1},${yStartingPoint - 90} ${chartCenter},${
                      yStartingPoint - 90
                    } ${chartCenter},${yStartingPoint - 110}`}
                    fill="none"
                    stroke="#999999"
                  />
                  <text
                    fill="#555555"
                    textAnchor="middle"
                    dominantBaseline="middle"
                    x={chartCenter}
                    y={yStartingPoint - 130}
                    className="fw-bold size-24"
                  >
                    {label}
                  </text>
                </g>
              );
            }

            if (customPriceLabel) {
              const x = customPriceLabel.end ? xVal + currentColumnWidth - 1 : xVal;
              const y1 = yStartingPoint - maxColumnHeight - 5;
              const y2 = customPriceLabel.label
                ? yStartingPoint + strokeLength[device] * 3.5
                : yStartingPoint + strokeLength[device];

              labelEl = (
                <g>
                  <line x1={x} y1={y1} x2={x} y2={y2} stroke="#cccccc" />
                  {!!customPriceLabel.label && (
                    <text
                      fill="#555555"
                      textAnchor="middle"
                      dominantBaseline="middle"
                      x={x}
                      y={y2 + labelVerticalShift[device]}
                      className="fw-bold medium"
                    >
                      {customPriceLabel.label}
                    </text>
                  )}
                </g>
              );
            }

            if (selectedPriceIndex === index) {
              const x1 = xVal + currentColumnWidth / 2;
              const x2 = chartWidth / 2;
              const firstCurveShift = x1 < x2 ? 5 : -5;
              const secondCurveShift = x1 < x2 ? -5 : 5;

              labelEl = (
                <Fragment>
                  {labelEl}
                  <g>
                    {isCurvedLine && x1 !== x2 ? (
                      <path
                        fill="none"
                        stroke="#999999"
                        d={`M ${x1} ${yStartingPoint} L ${x1} 20 Q ${x1} 15 ${x1 + firstCurveShift} 15 L ${
                          x2 + secondCurveShift
                        } 15 Q ${x2} 15 ${x2} 10 L ${x2} 0`}
                      />
                    ) : (
                      <polyline
                        points={`${x1},${yStartingPoint} ${x1},15 ${x2},15 ${x2},0`}
                        fill="none"
                        stroke="#999999"
                      />
                    )}
                  </g>
                </Fragment>
              );
            }

            if (dealerPriceIndex === index && showPolyline && dealerPrice) {
              const x1 = xVal + currentColumnWidth / 2;

              labelEl = (
                <Fragment>
                  {labelEl}
                  <g>
                    <polyline
                      points={`${x1},${yStartingPoint} ${x1 - 6},${yStartingPoint + 12} ${x1 + 6},${
                        yStartingPoint + 12
                      } ${x1},${yStartingPoint}`}
                      fill="#007ee5"
                      stroke="#007ee5"
                    />
                  </g>
                </Fragment>
              );
            }

            return (
              <g key={xVal}>
                <path
                  fill={gradientConfigLayout ? null : color}
                  width={currentColumnWidth}
                  height={yVal}
                  x={xVal}
                  y={yVal}
                  d={`M ${xVal},${yStartingPoint} h ${currentColumnWidth} v -${yVal} h -${currentColumnWidth} Z`}
                />
                {labelEl}
              </g>
            );
          })}
        </g>
        {!!gradientConfig && (
          <g>
            <defs>
              <linearGradient id={`gradient-${filterRandomInt}`} x1="0%" y1="0%" x2="0%" y2="100%">
                {gradientConfig.colors.map(({ offset, stopColor, stopOpacity }, key) => (
                  <stop
                    key={`color-${key}`} // eslint-disable-line react/no-array-index-key
                    offset={offset}
                    stopColor={stopColor}
                    stopOpacity={stopOpacity}
                  />
                ))}
              </linearGradient>
            </defs>
            <polygon
              points={`${gradientConfig.startPos * singleColSpace},${yStartingPoint} ${
                gradientConfig.startPos * singleColSpace
              },0 ${gradientConfig.endPos * singleColSpace},0 ${
                gradientConfig.endPos * singleColSpace
              },${yStartingPoint}`}
              fill={`url(#gradient-${filterRandomInt})`}
            />
          </g>
        )}
        {isSelectedIndexDefined && (
          <Fragment>
            {isArrowPositionMark ? (
              <g>
                {/* the triangle used to plot price position on the graph */}
                <defs>
                  <filter id={`shadow-${filterRandomInt}`}>
                    <feDropShadow dx={0} dy={0} stdDeviation={1} floodColor="black" />
                  </filter>
                </defs>
                <polygon
                  points={`${selectedIndexXMiddleCoord},${yStartingPoint} ${
                    selectedIndexXMiddleCoord + halfArrowSize
                  },${yStartingPoint - arrowSize} ${
                    selectedIndexXMiddleCoord - halfArrowSize
                  },${yStartingPoint - arrowSize}`}
                  fill="#ffffff"
                  stroke="#999999"
                  strokeWidth={'0.5'}
                  filter={`url(#shadow-${filterRandomInt})`}
                />
              </g>
            ) : (
              <circle
                cx={selectedIndexXMiddleCoord}
                cy={yStartingPoint}
                r={r1}
                fill="#ffffff"
                stroke="#0069bf"
                strokeWidth={r2}
              />
            )}
          </Fragment>
        )}
        {!isSelectedIndexDefined && isArrowPositionMark && !hideNoIndexArrow && (
          <g>
            {/* the triangle at the far end of the x-axis when no price */}
            <polygon
              points={`${columnsEdge},${yStartingPoint} ${columnsEdge + halfArrowSize},${
                yStartingPoint - arrowSize
              } ${columnsEdge - halfArrowSize},${yStartingPoint - arrowSize}`}
              fill="#ffffff"
              stroke="#999999"
              strokeWidth={'1'}
            />
          </g>
        )}
        {showMarketRange && (
          <Fragment>
            <polyline
              points={`${greenRangeFirstColMiddle},${yStartingPoint + 45} ${greenRangeFirstColMiddle},${
                yStartingPoint + 67
              } ${greenRangeFirstColMiddle + (isMobile ? 5 : 30)},${yStartingPoint + 67}`}
              fill="none"
              stroke="#979797"
            />
            <polyline
              points={`${greenRangeLastColMiddle},${yStartingPoint + 45} ${greenRangeLastColMiddle},${
                yStartingPoint + 67
              } ${greenRangeLastColMiddle - (isMobile ? 5 : 30)},${yStartingPoint + 67}`}
              fill="none"
              stroke="#979797"
            />
            <text
              fill="#555555"
              textAnchor="middle"
              dominantBaseline="middle"
              x={(greenRangeFirstColStart + greenRangeLastColEnd) / 2}
              y={yStartingPoint + 67}
              className="medium fw-bold"
            >
              Market range
            </text>
          </Fragment>
        )}
        {customLabels.map(({ label, position }) => (
          <text
            key={label}
            fill="#555555"
            textAnchor="middle"
            dominantBaseline="middle"
            x={`${position}%`}
            y={yStartingPoint + labelVerticalShift[device]}
            className="medium"
          >
            {label}
          </text>
        ))}
      </svg>
    </div>
  );
};

PricingChart.propTypes = {
  pricingData: PropTypes.shape({
    esp: PropTypes.string,
    msrp: PropTypes.string,
    lowEnd: PropTypes.string,
    highEnd: PropTypes.string,
  }).isRequired,
  isMobile: PropTypes.bool,
  dataPoints: PropTypes.arrayOf(PropTypes.number), // Defines height of each column
  keyPointsIndex: PropTypes.shape({
    lowEnd: PropTypes.number,
    highEnd: PropTypes.number,
    esp: PropTypes.number,
    msrp: PropTypes.number,
  }),
  primaryRangeColor: PropTypes.string,
  secondaryRangeColor: PropTypes.string,
  greatRangeColor: PropTypes.string,
  goodRangeColor: PropTypes.string,
  fairRangeColor: PropTypes.string,
  columnWidth: PropTypes.shape({
    mobile: PropTypes.number,
    desktop: PropTypes.number,
  }),
  columnPadding: PropTypes.number,
  strokeLength: PropTypes.shape({
    mobile: PropTypes.number,
    desktop: PropTypes.number,
  }),
  yRate: PropTypes.number,
  yStartingPoint: PropTypes.number,
  chartHeight: PropTypes.number,
  customLabels: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      position: PropTypes.number, // Percents
    })
  ),
  customPriceLabels: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      index: PropTypes.number,
      end: PropTypes.bool,
    })
  ),
  selectedPriceIndex: PropTypes.number,
  showMarketRange: PropTypes.bool,
  selectedPricePointRadius: PropTypes.number,
  highlightPrimaryRange: PropTypes.bool,
  labelVerticalShift: PropTypes.shape({
    mobile: PropTypes.number,
    desktop: PropTypes.number,
  }),
  isArrowPositionMark: PropTypes.bool,
  hideNoIndexArrow: PropTypes.bool,
  arrowSize: PropTypes.number,
  isCurvedLine: PropTypes.bool,
  filterRandomInt: PropTypes.number,
  gradientConfig: PropTypes.shape({
    startPos: PropTypes.number,
    endPos: PropTypes.number,
    colors: PropTypes.arrayOf(
      PropTypes.shape({
        offset: PropTypes.string,
        stopColor: PropTypes.string,
        stopOpacity: PropTypes.string,
      })
    ),
  }),
  gradientConfigLayout: PropTypes.arrayOf(
    PropTypes.shape({
      startPos: PropTypes.number,
      endPos: PropTypes.number,
      colors: PropTypes.arrayOf(
        PropTypes.shape({
          offset: PropTypes.string,
          stopColor: PropTypes.string,
          stopOpacity: PropTypes.string,
        })
      ),
    })
  ),
  xAxisColor: PropTypes.string,
  dealerPriceIndex: PropTypes.number,
  dealerPrice: PropTypes.number,
  showPolyline: PropTypes.bool,
};
